import type { Article } from "@newsfeed/shared";
import type { ApiClient } from "./api.js";

/** Detail carried by nf-article-row intent events. */
export interface ArticleIntent {
  article: Article;
  read?: boolean;
  kept?: boolean;
}

function intentOf(event: Event): ArticleIntent | null {
  // These events originate from nf-article-row with a known detail shape.
  return event instanceof CustomEvent ? (event.detail as ArticleIntent) : null;
}

/**
 * Shared handlers for the article intent events used by the stream, search and
 * kept views. `reload` refreshes the view's data after a state change.
 *
 *   - open : marks the article read and clears the `updated` flag server-side.
 *   - read : sets read/unread to the requested next state.
 *   - keep : sets kept to the requested next state.
 */
export function createArticleHandlers(
  api: ApiClient,
  reload: () => void | Promise<void>,
  onPending?: (id: string, action: "read" | "keep" | null) => void,
) {
  return {
    async open(event: Event): Promise<void> {
      const intent = intentOf(event);
      if (!intent) return;
      await api.openArticle(intent.article.id);
      await reload();
    },
    async read(event: Event): Promise<void> {
      const intent = intentOf(event);
      if (!intent) return;
      onPending?.(intent.article.id, "read");
      try {
        await api.setRead(intent.article.id, intent.read ?? false);
        await reload();
      } finally {
        onPending?.(intent.article.id, null);
      }
    },
    async keep(event: Event): Promise<void> {
      const intent = intentOf(event);
      if (!intent) return;
      onPending?.(intent.article.id, "keep");
      try {
        await api.setKept(intent.article.id, intent.kept ?? false);
        await reload();
      } finally {
        onPending?.(intent.article.id, null);
      }
    },
  };
}
